// src/components/TopHeader.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCog, FaSignOutAlt, FaComments, FaRobot } from 'react-icons/fa';
import { useChat } from '../contexts/ChatContext.jsx';
import { isClientUser, readStoredUser } from '../utils/userFlags.js';
import './TopHeader.css';

const TopHeader = () => {
  const API = import.meta.env.VITE_API_URL;
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const assistantButtonRef = useRef(null);
  const dmButtonRef = useRef(null);
  const [pageTitle, setPageTitle] = useState('');
  const [pageSubtitle, setPageSubtitle] = useState('');
  const [pageStatus, setPageStatus] = useState('');
  const [companyName, setCompanyName] = useState('');
  const {
    isChatOpen,
    setIsChatOpen,
    assistantNeedsAttention,
    setAssistantNeedsAttention,
    setAssistantAnchorRect,
    isDmOpen,
    setIsDmOpen,
    setDmAnchorRect,
    unreadCount,
  } = useChat();

  // Keep the CSS var in sync with the real fixed header height, so pages that size themselves
  // using `var(--top-header-height)` don't get clipped.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const update = () => {
      try {
        const h = Math.ceil(el.getBoundingClientRect().height || 0);
        if (h > 0) document.documentElement.style.setProperty('--top-header-height', `${h}px`);
      } catch {}
    };

    update();
    window.addEventListener('resize', update);

    let ro;
    try {
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => update());
        ro.observe(el);
      }
    } catch {}

    return () => {
      window.removeEventListener('resize', update);
      try { ro?.disconnect?.(); } catch {}
    };
  }, []);

  // Load company name (appconfig.companyname) for display in the center of the top bar.
  useEffect(() => {
    let cancelled = false;
    const url = API ? `${API}/api/appconfig` : '/api/appconfig';
    (async () => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const cfg = await res.json().catch(() => ({}));
        if (cancelled) return;
        const name = String(cfg?.companyname || '').trim();
        setCompanyName(name);
      } catch {
        if (!cancelled) setCompanyName('');
      }
    })();

    const onCfgUpdated = (e) => {
      const { key, value } = e?.detail || {};
      if (String(key || '').toLowerCase() !== 'companyname') return;
      const next = String(value || '').trim();
      setCompanyName(next);
    };
    window.addEventListener('appconfig:updated', onCfgUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener('appconfig:updated', onCfgUpdated);
    };
  }, [API]);

  useEffect(() => {
    const onSetTitle = (e) => {
      const { title = '', subtitle = '' } = e?.detail || {};
      setPageTitle(String(title || ''));
      setPageSubtitle(String(subtitle || ''));
    };
    const onClearTitle = () => {
      setPageTitle('');
      setPageSubtitle('');
    };
    const onSetStatus = (e) => {
      const { status = '' } = e?.detail || {};
      setPageStatus(String(status || ''));
    };
    const onClearStatus = () => setPageStatus('');
    window.addEventListener('topbar:setTitle', onSetTitle);
    window.addEventListener('topbar:clearTitle', onClearTitle);
    window.addEventListener('topbar:setStatus', onSetStatus);
    window.addEventListener('topbar:clearStatus', onClearStatus);
    return () => {
      window.removeEventListener('topbar:setTitle', onSetTitle);
      window.removeEventListener('topbar:clearTitle', onClearTitle);
      window.removeEventListener('topbar:setStatus', onSetStatus);
      window.removeEventListener('topbar:clearStatus', onClearStatus);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/logout`, { method: 'POST' });
    } catch {}
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('authToken');
      // Clear chat-related UI flags
      localStorage.removeItem('chat.agent.open');
      localStorage.removeItem('chat.dm.open');
      localStorage.removeItem('chat.dm.activeUserId');
    } catch {}
    try { window.dispatchEvent(new CustomEvent('auth:logout')); } catch {}
    navigate('/');
  };

  const user = (() => {
    try {
      return readStoredUser();
    } catch { return {}; }
  })();
  const cid = user?.clientid ?? user?.clientId ?? user?.clientID ?? null;
  const IS_CLIENT = isClientUser(user);

  const handleChatOpen = () => {
    try {
      const rect = dmButtonRef.current?.getBoundingClientRect?.();
      if (rect) {
        setDmAnchorRect({
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        });
      }
      setIsDmOpen(!isDmOpen);
    } catch { /* noop */ }
  };

  const handleAssistantOpen = () => {
    try {
      const rect = assistantButtonRef.current?.getBoundingClientRect?.();
      if (rect) {
        setAssistantAnchorRect({
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        });
      }
      if (isChatOpen) setAssistantNeedsAttention(false);
      setIsChatOpen(!isChatOpen);
    } catch { /* noop */ }
  };

  const displayName = (user && (user.fullname || user.name || user.login)) || '';

  return (
    <header className="top-header" ref={headerRef}>
      <div className="topbar-left">
        {!!pageTitle && (
          <h1 className="topbar-title">
            {pageTitle}
            {!!pageSubtitle && <span className="topbar-subtitle">{pageSubtitle}</span>}
          </h1>
        )}
      </div>
      <div className="topbar-center">
        {!!companyName && (
          <div className="topbar-company" title={companyName}>{companyName}</div>
        )}
        {!!pageStatus && (
          <div className="topbar-status" title={pageStatus}>{pageStatus}</div>
        )}
      </div>
      <div className="top-header-user">
        {displayName ? (
          <>
            <span className="user-greeting">Welcome, {user.fullname || user.name || user.login || clientDisplayName}</span>
            <button
              className="icon-button"
              onClick={() => navigate('/settings')}
              title="Settings"
              aria-label="Settings"
            >
              <FaCog />
            </button>

            {!IS_CLIENT && (
              <button
                ref={assistantButtonRef}
                className={`icon-button ${isChatOpen ? 'is-active' : ''} ${assistantNeedsAttention ? 'attention-shake' : ''}`}
                onClick={handleAssistantOpen}
                title="Assistant"
                aria-label="Assistant"
                aria-pressed={isChatOpen}
              >
                <FaRobot />
              </button>
            )}

            {!IS_CLIENT && (
              <button
                ref={dmButtonRef}
                className={`icon-button chat-with-badge ${isDmOpen ? 'is-active' : ''} ${unreadCount > 0 ? 'attention-shake has-unread' : ''}`}
                onClick={handleChatOpen}
                title="Chat"
                aria-label="Chat"
                aria-pressed={isDmOpen}
              >
                <FaComments />
                {unreadCount > 0 && (
                  <span className="chat-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </button>
            )}
            
            <button
              className="icon-button"
              onClick={handleLogout}
              title="Logout"
              aria-label="Logout"
            >
              <FaSignOutAlt />
            </button>
          </>
        ) : (
          <span className="user-greeting">Not logged in</span>
        )}
      </div>
    </header>
  );
};

export default TopHeader;
